import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Languages, LogOut, Menu, Moon, Sun, User } from 'lucide-react';
import { ProductBrand } from '@barahpay/shared';
import { useAuth } from '../auth/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useThemeMode } from '../theme/ThemeContext';

/**
 * The Barah Plus header: no bar of its own, just floating pills on the page canvas — the language
 * toggle, the theme switch and the profile pill — with the menu button and a compact lockup on
 * narrow screens.
 */
export function TopNav({ mobile, collapsed, onOpenMenu }: { mobile: boolean; collapsed: boolean; onOpenMenu: () => void }) {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const { mode, toggleMode } = useThemeMode();

  return (
    <header className={`bp-topnav${collapsed ? ' is-collapsed' : ''}`}>
      <div className="bp-topnav__start">
        {mobile ? (
          <>
            <button type="button" className="bp-chrome-btn bp-chrome-btn--lg" onClick={onOpenMenu} aria-label={t('common.openMenu')}>
              <Menu size={24} />
            </button>
            <ProductBrand size="sm" />
          </>
        ) : null}
      </div>

      <div className="bp-topnav__end">
        <button
          type="button"
          className="bp-lang-toggle"
          onClick={toggleLanguage}
          aria-label={t('common.switchLanguage')}
          title={t('common.switchLanguage')}
        >
          <Languages size={20} aria-hidden />
          <span>{language === 'en' ? 'AR' : 'EN'}</span>
        </button>

        <button
          type="button"
          className="bp-pill-btn"
          onClick={toggleMode}
          aria-label={t('common.theme')}
          title={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}
        >
          {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        <ProfileMenu />
      </div>
    </header>
  );
}

function ProfileMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointer = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const role = user?.roles[0];

  return (
    <div className="bp-profile" ref={ref}>
      <button
        type="button"
        className="bp-profile__pill"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="bp-profile__avatar">
          <User size={16} aria-hidden />
        </span>
        <span className="bp-profile__who">
          <span className="bp-profile__name">{user?.fullName}</span>
          {role ? <span className="bp-profile__role">{role}</span> : null}
        </span>
        <ChevronDown size={16} className={`bp-profile__chevron${open ? ' is-open' : ''}`} aria-hidden />
      </button>

      {open ? (
        <div className="bp-profile__menu" role="menu">
          {/* On a phone the pill has no room for the name, so the menu carries it. */}
          <div className="bp-profile__menu-head">
            <span className="bp-profile__name">{user?.fullName}</span>
            <span className="bp-profile__role">{user?.email}</span>
          </div>

          <button
            type="button"
            role="menuitem"
            className="bp-profile__item"
            onClick={() => {
              setOpen(false);
              navigate('/profile');
            }}
          >
            <User size={16} aria-hidden />
            {t('common.profile')}
          </button>

          <div className="bp-profile__divider" />

          <button
            type="button"
            role="menuitem"
            className="bp-profile__item bp-profile__item--danger"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            <LogOut size={16} aria-hidden />
            {t('common.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
